import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import {
  SectionHeading,
  Loading,
  ErrorBanner,
  SuccessBanner,
} from "../components/ui";

interface Question {
  id: string;
  section: string | null;
  questionText: string;
  questionType: "short_text" | "paragraph" | "select";
  options: string[] | null;
  required: boolean;
  sortOrder: number;
}

interface SurveyResp {
  survey: { id: string; title: string; description: string | null; eventTitle: string };
  questions: Question[];
  alreadySubmitted: boolean;
  submittedAt: string | null;
  myAnswers: Record<string, string>;
}

export function SurveyAnswerPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [data, setData] = useState<SurveyResp | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    api
      .get<SurveyResp>(`/surveys/${eventId}`)
      .then((d) => {
        setData(d);
        setAnswers(d.myAnswers ?? {});
      })
      .catch((e) => setLoadErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <Loading />;

  if (loadErr || !data) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link to="/" className="text-sm text-tora-ink/60 hover:text-tora">
          ← トップへ
        </Link>
        <ErrorBanner>{loadErr ?? "アンケートを表示できません"}</ErrorBanner>
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/surveys/${eventId}/submit`, {
        answers: Object.entries(answers).map(([questionId, answerText]) => ({
          questionId,
          answerText,
        })),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // セクションごとにグルーピング
  const sections: { name: string; questions: Question[] }[] = [];
  for (const q of data.questions) {
    const name = q.section ?? "";
    let sec = sections.find((s) => s.name === name);
    if (!sec) {
      sec = { name, questions: [] };
      sections.push(sec);
    }
    sec.questions.push(q);
  }

  return (
    <article className="mx-auto max-w-2xl space-y-5">
      <Link
        to={`/events/${eventId}`}
        className="inline-block text-sm text-tora-ink/60 hover:text-tora"
      >
        ← イベント詳細へ
      </Link>

      <header className="space-y-2">
        <p className="text-[11px] text-tora-accent tracking-widest font-medium">
          {data.survey.eventTitle}
        </p>
        <SectionHeading level={2}>{data.survey.title}</SectionHeading>
        {data.survey.description && (
          <p className="text-sm text-tora-ink/70 leading-relaxed">
            {data.survey.description}
          </p>
        )}
      </header>

      {done ? (
        <SuccessBanner>
          回答を送信しました。ご協力ありがとうございました。
        </SuccessBanner>
      ) : (
        <>
          {data.alreadySubmitted && (
            <SuccessBanner>
              すでに回答済みです。内容を修正して再送信することもできます。
            </SuccessBanner>
          )}

          {sections.map((sec) => (
            <section key={sec.name} className="card p-5 md:p-6 space-y-4">
              {sec.name && (
                <h3 className="font-bold text-tora border-b border-black/5 pb-2">
                  {sec.name}
                </h3>
              )}
              {sec.questions.map((q) => (
                <div key={q.id}>
                  <label className="label">
                    {q.questionText}
                    {q.required && <span className="ml-1 text-red-600">*</span>}
                  </label>
                  {q.questionType === "short_text" && (
                    <input
                      className="input"
                      value={answers[q.id] ?? ""}
                      onChange={(e) =>
                        setAnswers({ ...answers, [q.id]: e.target.value })
                      }
                    />
                  )}
                  {q.questionType === "paragraph" && (
                    <textarea
                      className="input min-h-[90px]"
                      value={answers[q.id] ?? ""}
                      onChange={(e) =>
                        setAnswers({ ...answers, [q.id]: e.target.value })
                      }
                    />
                  )}
                  {q.questionType === "select" && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(q.options ?? []).map((o) => (
                        <button
                          key={o}
                          type="button"
                          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                            answers[q.id] === o
                              ? "bg-tora text-white"
                              : "bg-black/5 text-tora-ink/70 hover:bg-black/10"
                          }`}
                          onClick={() => setAnswers({ ...answers, [q.id]: o })}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </section>
          ))}

          {err && <ErrorBanner>{err}</ErrorBanner>}
          <button
            className="btn-primary btn-lg w-full md:w-auto md:px-10"
            onClick={submit}
            disabled={busy}
          >
            {busy ? "送信中…" : data.alreadySubmitted ? "回答を更新する →" : "回答を送信する →"}
          </button>
        </>
      )}
    </article>
  );
}
